import * as React from 'react';

import type { BlockMathData, InlineMathData } from '@gitbook/doc-core';
import type { Block, Inline } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { AutoSizeInput, AutoSizeTextarea, Button, usePopoverClose } from '@gitbook/spine-react';
import { useStateWithDeps } from '@gitbook/util-react-hooks';

/**
 * Body of a popover for editing a math.
 */
export function MathEditPopover(props: {
    editor: EditorContainer;
    node: Inline | Block;
}): React.ReactElement {
    const { node, editor } = props;

    const closePopover = usePopoverClose();
    const { formula } = node.data.toFrozenObject() as InlineMathData | BlockMathData;

    // We don't directly update the formula data on the node as the node will be normalized out
    // if the user empties the formula.
    const [value, setValue] = useStateWithDeps(formula, [formula]);
    React.useEffect(() => {
        if (!value.trim()) {
            return;
        }

        editor.change((change) => {
            change.setDataByKey(node.key, ['formula'], value);
        });
    }, [editor, node.key, value]);

    const Input = node.object === 'block' ? AutoSizeTextarea : AutoSizeInput;

    return (
        <div className="flex flex-col gap-2">
            <Input
                placeholder="Type your math formula here"
                className="min-w-80 max-w-3xl px-4 py-2"
                aria-label="Edit Math formula"
                value={value}
                onChange={(event) => {
                    setValue(event.target.value);
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        if (node.object === 'block') {
                            if (!event.shiftKey || event.metaKey) {
                                closePopover();
                            }
                        } else {
                            closePopover();
                        }
                    }
                }}
            />

            <div className="flex self-end">
                <Button
                    size="small"
                    kind="secondary"
                    leadingIcon={Icon.Check}
                    label="Confirm"
                    tooltip={{ label: 'In GitBook, changes are automatically saved!' }}
                    iconOnly
                    onPress={closePopover}
                />
            </div>
        </div>
    );
}
